<template>
  <div class="bg-white rounded-lg border border-growth-50 overflow-hidden">
    <div class="px-4 py-3 border-b border-growth-50">
      <h3 class="text-sm font-semibold text-growth-900">Payment Insights</h3>
    </div>

    <div v-if="loading" class="p-8 text-center text-growth-600">
      <svg class="animate-spin h-5 w-5 mx-auto mb-2" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
      Loading spending breakdown...
    </div>

    <div v-else-if="procedures.length" class="p-4">
      <!-- Breadcrumb -->
      <div v-if="drillLevel > 0" class="flex items-center gap-1.5 text-xs text-growth-600 mb-4">
        <button @click="drillLevel = 0; selectedCategory = null; selectedSubcatCode = null" class="hover:text-growth-tangerine transition-colors">All Categories</button>
        <template v-if="selectedCategory">
          <span class="mx-1">/</span>
          <button @click="drillLevel = 1; selectedSubcatCode = null" class="hover:text-growth-tangerine transition-colors">{{ selectedCategory }}</button>
        </template>
        <template v-if="selectedSubcatCode">
          <span class="mx-1">/</span>
          <span class="text-growth-900 font-medium">{{ selectedSubcatName }}</span>
        </template>
      </div>

      <!-- Level 0: Category pie -->
      <Pie_Chart
        v-if="drillLevel === 0"
        :key="'cat'"
        :chart_data="categoryChartData"
        :chart_config="pieCfg"
        override-height="h-[350px]"
        @slice-click="onCategoryClick"
      />

      <!-- Level 1: Subcategory pie -->
      <Pie_Chart
        v-else-if="drillLevel === 1"
        :key="'sub-' + selectedCategory"
        :chart_data="subcategoryChartData"
        :chart_config="pieCfg"
        override-height="h-[350px]"
        @slice-click="onSubcategoryClick"
      />

      <!-- Level 2: HCPCS pie -->
      <Pie_Chart
        v-else-if="drillLevel === 2 && hcpcsChartData.values.length"
        :key="'hcpcs-' + selectedSubcatCode"
        :chart_data="hcpcsChartData"
        :chart_config="pieCfg"
        override-height="h-[350px]"
      />
      <div v-else-if="drillLevel === 2" class="p-4 text-center text-sm text-growth-600">No procedures in this subcategory</div>
    </div>

    <div v-else class="p-6 text-center text-sm text-growth-600">No spending data available</div>
  </div>
</template>

<script>
import Pie_Chart from '@/components/widgetcomponents/Viz_Elements/Pie_Chart.vue'

const COLORS = ['#F97140', '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EC4899', '#06B6D4', '#6B7280',
                '#EF4444', '#14B8A6', '#A855F7', '#F97316']

export default {
  name: 'PracticePaymentInsights',
  components: { Pie_Chart },
  props: {
    pacId: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      procedures: [],
      drillLevel: 0,
      selectedCategory: null,
      selectedSubcatCode: null,
      pieCfg: {
        margin: { top: 60, right: 80, bottom: 60, left: 80 },
        innerRadius: 60,
        padAngle: 0.02,
        cornerRadius: 3,
        labelDisplay: { showLabels: true, showValues: false, minAngleForLabel: 0.08, minPercentageForLabel: 3, labelOffset: 20 },
        legend: { display: false },
        formatting: { percentage: false, sig_figs: 0 },
        tooltip: { enabled: true },
      },
    }
  },
  computed: {
    // Group procedures into categories
    categories() {
      const map = {}
      for (const p of this.procedures) {
        const cat = p.category || 'Other'
        if (!map[cat]) map[cat] = { name: cat, total: 0 }
        map[cat].total += (p.medicare || 0) + (p.medicaid || 0)
      }
      return Object.values(map).sort((a, b) => b.total - a.total)
    },
    grandTotal() {
      return this.categories.reduce((s, c) => s + c.total, 0)
    },
    categoryChartData() {
      return {
        values: this.categories.map(c => c.total),
        labels: this.categories.map(c => `${c.name}  ${this.fmtCur(c.total)} (${this.pctOf(c.total, this.grandTotal)}%)`),
        colors: COLORS.slice(0, this.categories.length),
      }
    },

    // Subcategories within selected category
    subcategories() {
      if (!this.selectedCategory) return []
      const map = {}
      for (const p of this.procedures) {
        if (p.category !== this.selectedCategory) continue
        const key = p.subcat_code
        if (!map[key]) map[key] = { code: key, name: p.subcat_name || key, total: 0 }
        map[key].total += (p.medicare || 0) + (p.medicaid || 0)
      }
      return Object.values(map).sort((a, b) => b.total - a.total)
    },
    subcatTotal() {
      return this.subcategories.reduce((s, c) => s + c.total, 0)
    },
    subcategoryChartData() {
      return {
        values: this.subcategories.map(s => s.total),
        labels: this.subcategories.map(s => `${s.name}  ${this.fmtCur(s.total)} (${this.pctOf(s.total, this.subcatTotal)}%)`),
        colors: COLORS.slice(0, this.subcategories.length),
      }
    },
    selectedSubcatName() {
      const s = this.subcategories.find(s => s.code === this.selectedSubcatCode)
      return s ? s.name : this.selectedSubcatCode
    },

    // HCPCS within selected subcategory
    hcpcsList() {
      if (!this.selectedSubcatCode) return []
      return this.procedures
        .filter(p => p.subcat_code === this.selectedSubcatCode)
        .sort((a, b) => ((b.medicare || 0) + (b.medicaid || 0)) - ((a.medicare || 0) + (a.medicaid || 0)))
    },
    hcpcsTotal() {
      return this.hcpcsList.reduce((s, p) => s + (p.medicare || 0) + (p.medicaid || 0), 0)
    },
    hcpcsChartData() {
      return {
        values: this.hcpcsList.map(p => (p.medicare || 0) + (p.medicaid || 0)),
        labels: this.hcpcsList.map(p => {
          const combined = (p.medicare || 0) + (p.medicaid || 0)
          const label = p.description || p.code
          return `${p.code} ${label}  ${this.fmtCur(combined)} (${this.pctOf(combined, this.hcpcsTotal)}%)`
        }),
        colors: COLORS.slice(0, this.hcpcsList.length),
      }
    },
  },
  watch: {
    pacId: {
      immediate: true,
      handler(id) {
        if (id) this.fetchSpending()
        else this.procedures = []
      },
    },
  },
  methods: {
    async fetchSpending() {
      this.loading = true
      this.drillLevel = 0
      this.selectedCategory = null
      this.selectedSubcatCode = null
      try {
        const response = await this.$axios.get(`/api/growth/practices/${this.pacId}/spending/`)
        this.procedures = response.data.procedures || []
      } catch (err) {
        console.error('Failed to load spending breakdown', err)
        this.procedures = []
      } finally {
        this.loading = false
      }
    },
    onCategoryClick(sliceData) {
      const cat = this.categories[sliceData.index]
      if (cat) {
        this.selectedCategory = cat.name
        this.selectedSubcatCode = null
        this.drillLevel = 1
      }
    },
    onSubcategoryClick(sliceData) {
      const sub = this.subcategories[sliceData.index]
      if (sub) {
        this.selectedSubcatCode = sub.code
        this.drillLevel = 2
      }
    },
    pctOf(val, total) {
      return total > 0 ? Math.round((val / total) * 100) : 0
    },
    fmtCur(n) {
      if (!n) return '$0'
      if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M'
      if (n >= 1000) return '$' + Math.round(n / 1000).toLocaleString() + 'K'
      return '$' + Math.round(n).toLocaleString()
    },
  },
}
</script>
